// this will handle communication on a parent recipe being
// customized by the customization-details, and customization-summary child components
import {Directive, EventEmitter, Injectable, OnDestroy, OnInit, Output} from '@angular/core';
import {NgRedux, select} from '@angular-redux/store';
import {ActivatedRoute, Router} from '@angular/router';

import {ProxyService} from '@core/services/proxy.service';
import {OfferService} from '@core/services/offer.service';
import {MenuUtilityService} from '@core/services/menu-utlities.services';
import {RewardItemService} from '@core/services/reward-item.service';
import {
    Actions,
    ChildRecipeEntity,
    ChildRecipeResponse,
    CustomizationGroupsEntity,
    CustomizationGroupType,
    IAppState,
    IngredientMultiplier,
    IngredientsEntity,
    LocationsEntity,
    ModifierGroupsEntity,
    ModifiersEntity,
    Order,
    ParentRecipeEntity,
    RecipeContext,
    SpecialInstructionsEntity,
    isModifierGroupDefaultSelected
} from '@core/redux/stores';

import {ApiRoutes} from '@shared/models/configs/api.endpoints';
import {Offer, OfferDiscountType, OfferPriceOverrideFilter} from '@features/account/models/offer.model';

import {ChildChanged, RecipeType, Sources} from '@features/customization/models';

import {Observable, Subscription} from 'rxjs';

import * as _ from 'lodash-es';
import {LoggingService} from '@core/services/logging.service';
import {CustomizationSubgroupService} from '@core/services/customization-subgroup.service';
import {GtmService, SelectPickupService, UtilitiesService} from '@app/core/services';
import {CartGTM} from '@app/shared/models/configs/gtm.external';
import {Constants} from '@app/core/constants/core.constants';
import { ConfirmModalService, ModalService } from '@core/services';

export interface RestrictedCustomizationGroup {
    customizationGroup: CustomizationGroupsEntity,
    selectedQuantity: number;
    minQuantityAttained: boolean;
    maxQuantityAttained: boolean;
    displayText: string;
  }

@Directive()
@Injectable()
export class CustomizationService implements OnInit, OnDestroy {
  // always observing the location. The user should always be forced to select a location
  // by the forced check location guard
  @select((appState) => appState.locationSelected) locationSel: Observable<LocationsEntity>;

  // the order redux is only used when the URL :source parameter is an Order source.
  @select((appState) => appState.activeOrder) activeOrder: Observable<Order>;
  // the order redux is only used for edit favorite.
  @select((appState) => appState.activeFavorite) activeFavorite: Observable<Order>;

  @Output() parentRecipe = new EventEmitter<ParentRecipeEntity>();
  @Output() childRecipe = new EventEmitter<ChildChanged>();
  @Output() linkedChildRecipe = new EventEmitter<ChildChanged>();
  @Output() toggleWhatsOnIt = new EventEmitter<boolean>();
  public parent: ParentRecipeEntity;
  public child: ChildRecipeEntity;
  private linkedChild: ChildRecipeEntity;

  private edittingOrder: Order;

  private pageId: number;
  public order: Order;

  private subscriptions = new Subscription();
  private locationId: number;

  private orderdCollection: Order;

  public isReward: boolean;
  public isOrderReward: boolean;

  public isOffer: boolean;
  private childRecipeId: Number;
  private recipeId: Number;

  private offerFilterMenu: any;
  public paramSource: any;
  public paramSplOpt: any;
  public quantityRestrictedCustomizationGroupList: RestrictedCustomizationGroup[] = [];
  constructor(
    private proxy: ProxyService,
    private router: Router,
    private menuUtil: MenuUtilityService,
    private ngRedux: NgRedux<IAppState>,
    private rewardItemService: RewardItemService,
    private offerService: OfferService,
    private logger: LoggingService,
    private customizationSubGroupService: CustomizationSubgroupService,
    private gtmService: GtmService,
    private selectPickupService: SelectPickupService,
    private modalService: ModalService,
    private confirmModalService: ConfirmModalService
  ) {
    this.pageId = 1; // default is to use the first page if none provided.
    this.child = new ChildRecipeEntity();
    this.child.ingredientsList = new Array<IngredientsEntity>();
    this.linkedChild = new ChildRecipeEntity();
    this.linkedChild.context = RecipeContext.LINKED;
    this.isReward = false;
    this.isOffer = false;
    this.subscriptions.add(
      this.locationSel.subscribe(
        (response) => {
          if (response) {
            this.locationId = response.id;
          }
        }
      )
    );

 }

  setPageId(pageId: number) {
    this.pageId = pageId;
    this.child.selectedScreen = this.pageId;
    this.childRecipeChanged(this.child, this);
  }

  ngOnInit() {
    if (this.locationId === undefined || this.locationId === null || this.locationId === 0) {
      this.router.navigate(['/locations'], { queryParams:
        UtilitiesService.generateQueryParams([Constants.RETURN_URL], [this.router.url])});
    }
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }
  sendViewItemGTMEvent(recipe: ChildRecipeEntity) {
    if(recipe && !recipe.isRecipeNotAvailable) {
      const viewItemEventPayload = this.gtmService.getGTMEventPayloadFromRecipe(recipe, 'view_item');
      this.gtmService.sendGenericEvent({ ecommerce: null });
      this.gtmService.sendGenericEvent(viewItemEventPayload);
    }
  }

  // parent recipe is the meal options basically
  getParentRecipe(pageId: number, childRecipeId: number, recipeId: number) {
    // URL changed from ~/locations/{locationId}/menu/categories/{categoryId}/recipes
    // to /locations/{locationId}/menu/recipes/{parentRecipeId}/child-recipes
    // As per performance enhancements.
    const apiRoute = ApiRoutes.LOCATIONS
      + this.locationId
      + ApiRoutes.GENERIC_MENU
      + ApiRoutes.MENU_CATEGORIES_RECIPES
      + '/' + recipeId
      + ApiRoutes.CHILD_RECIPE_ONLY;

    this.pageId = pageId;
    this.childRecipeId = childRecipeId;
    this.recipeId = recipeId;
    console.log('api route', apiRoute);

    return this.proxy.httpGet(apiRoute).toPromise().then(
      (response) => {
        // const listOfRecipes = <(Array<ParentRecipeEntity>)>response.body.valueOf();

        // validate whether location has Menu Item data
        if (response.body !== null) {
          const childRecipes = <(ParentRecipeEntity)>response.body.valueOf();
          if (childRecipes) {
            this.parent = childRecipes;
            if (this.isReward && this.rewardItemService.currentRewardItem) {
              this.parent.recipes = this.parent.recipes.filter(recipe =>
                this.rewardItemService.currentRewardItem.childRecipeIds.includes(recipe.id));
            } else if(this.isReward && childRecipeId) {
              this.parent.recipes = this.parent.recipes.filter(r => r.id == childRecipeId);
            } else if(this.isOrderReward && this.child?.useReward) {
              this.parent.recipes = this.parent.recipes.filter(r => r.id == this.child?.id);
            }
            if (this.isOffer && this.parent) {
              this.parent.recipes = this.getOfferRecipes(this.parent.recipes);
            }
            this.updateAndEmitParentRecipe();
          }
        } else {

            if (this.locationId !== undefined && this.locationId !== null && this.locationId !== 0) {
              this.router.navigate(['/locations/' + this.locationId + '/menu'], { queryParams:
                UtilitiesService.generateQueryParams([Constants.MENU_NOT_AVAILABLE], [true])});
            }
        }
        // this.setDefaultChildRecipeId();
      },
      (error) => {
        // TODO handle error when returned from server

        const offer = this.offerService.offerSelectedData || null;
        if (offer && offer.id) {
          switch (error.error.code) {
            case 2001:
              this.confirmModalService.show(
                'Offer Not Valid at Selected Location',
                '',
                'Please select an alternate location to redeem this offer.',
                'OK, Got it.',
                '',
                '',
                true,
                'offerNotAvailable',
                '',
                'cancelOfferNotAvailable'
              );
              break;
          }
        }

        this.logger.logError(error);
      }
    );
  }

  private updateAndEmitParentRecipe() {
    sessionStorage.setItem('mealOptions', JSON.stringify(this.parent));

    this.parentRecipe.emit(this.parent);
  }

  // this is called only the first ti  when the page loads. It finds the default or first child recipe associated with the
  // parent recipe group. This is typically the "meal" recipe option.
  setDefaultChildRecipeId(defaultRecipeId, recipeContext = RecipeContext.CHILD, orderItemId = null, upsell = false) {
    let dRecpId = null;
    const defaultRecipe = this.parent?.recipes?.find((x) => x.isDefaultChild === true);
    if (!defaultRecipe) {
      dRecpId = defaultRecipeId ? defaultRecipeId : this.parent?.recipes[0]?.id;
    } else {
      dRecpId = defaultRecipeId ? defaultRecipeId : defaultRecipe.id;
    }
    // if(recipeContext == RecipeContext.LINKED && this.child. )
    this.getDefaultMenuChildRecipeToCustomize(dRecpId, null, recipeContext, orderItemId, upsell);
  }

  setChildFromSessionStorage(isLinkedRecipe = false) {
    this.child = _.cloneDeep(JSON.parse(sessionStorage.getItem('childRecipe')));
    sessionStorage.setItem('isPageRefresh', 'true');
    this.childRecipeChanged(this.child, this);
  }

  getDefaultMenuChildRecipeToCustomize(childRecipeId: number, recipeToCopyCustomizationsFrom: ChildRecipeEntity = null, recipeContext = RecipeContext.CHILD, orderItemId = null, upsell: boolean = false, changedLinkedChildMeal = null, fromComponent = null) {
    let apiEndpoint = ApiRoutes.LOCATIONS + this.locationId + ApiRoutes.CHILD_RECIPE + '/' + childRecipeId;
    let recipeTypeId = null;
    if(changedLinkedChildMeal) {
      const childRecipe = JSON.parse(sessionStorage.getItem('childRecipe'));
      if(childRecipe?.linkedRecipe) {
        recipeTypeId = childRecipe.recipeTypeId;
      }
    }
    apiEndpoint = recipeContext== RecipeContext.LINKED ? apiEndpoint + '?recipetypeid=' + (recipeTypeId ?? (this.child?.recipeTypeId ?? RecipeType.MediumMeal)) : apiEndpoint;
    this.subscriptions.add(
      this.proxy.httpGet(apiEndpoint).subscribe(
        (success) => {
          let successResponse = <ChildRecipeResponse>success.body.valueOf();
          if (successResponse?.recipe) {
             if(recipeContext == RecipeContext.LINKED) {
              if(!upsell) {
                successResponse.recipe.isUpsell = false;
                successResponse.recipe.customizationGroups = successResponse.recipe.customizationGroups.filter(cg => !cg.isUpsell);
              }
             }
            this.processChildRecipe(successResponse, recipeToCopyCustomizationsFrom, recipeContext, orderItemId, changedLinkedChildMeal, fromComponent);
          }
        },
        (error) => {
          this.logger.logError(error);
        }
      )
    );
  }
  private processChildRecipe(successResponse: ChildRecipeResponse, recipeToCopyCustomizationsFrom: ChildRecipeEntity, recipeContext, orderItemId = null, changedLinkedChildMeal = false, fromComponent = null) {
    this.setDefaultChildValues(successResponse, recipeContext);
    if (recipeToCopyCustomizationsFrom) {
      this.copyCustomizations(recipeToCopyCustomizationsFrom, changedLinkedChildMeal ? RecipeContext.LINKED : RecipeContext.CHILD);
      if(recipeToCopyCustomizationsFrom?.orderItemId && this.child) {
        this.child.orderItemId = recipeToCopyCustomizationsFrom.orderItemId;
      }
    }
    if(recipeContext == RecipeContext.CHILD) {
      this.childRecipeChanged(this.child, fromComponent ?? this);
    } else if(recipeContext == RecipeContext.LINKED) {
      this.linkedChild.orderItemId = orderItemId;
      this.linkedChildChanged(this.linkedChild, fromComponent ?? this);
    }
  }
  linkedChildChanged(data: ChildRecipeEntity, fromComponent: any) {
    data.context = RecipeContext.LINKED;
    if (!(fromComponent instanceof CustomizationService)) {
      this.linkedChild = _.cloneDeep(data);
    }
    this.linkedChild = <ChildRecipeEntity>this.menuUtil.updateRecipeIncludedInfo(this.linkedChild);
    this.calculateCaloriesAndPrice(this.linkedChild);
    this.checkIfDrinkExist(this.linkedChild);
    this.linkedChildRecipe.emit({ childRecipe: this.linkedChild, from: fromComponent });
  }
  public updateOuterChildLinkedRecipeId(child: any, linkedRecipe: ChildRecipeEntity) {
    child.customizationGroups.forEach(cg => {
      const linkedRecipeModifierGroup = cg.modifierGroups.find(mg => mg.linkedRecipeId == linkedRecipe.id);
      if (!linkedRecipeModifierGroup) {
        // Linked recipe at the parent level is different from the selected child recipe id
        const existingLinkedRecipeModifierGroup = cg.modifierGroups.find(mg => mg.isSelected && mg.linkedRecipeId && mg?.modifiers?.some(m => m.selectedChildRecipeId));
        if (existingLinkedRecipeModifierGroup) {
          existingLinkedRecipeModifierGroup.linkedRecipeId = linkedRecipe.id;
        }
      }
    });
  }
  copyCustomizations(recipeToCopyCustomizationsFrom: ChildRecipeEntity, recipeContext = RecipeContext.CHILD) {
    if (recipeToCopyCustomizationsFrom && (recipeContext == RecipeContext.LINKED ? recipeToCopyCustomizationsFrom.id !== this.linkedChild.id : recipeToCopyCustomizationsFrom.id !== this.child.id)) {
      _.flatMap(recipeToCopyCustomizationsFrom?.customizationGroups?.filter(cg => cg.type !== CustomizationGroupType.Side &&
         cg.type !== CustomizationGroupType.Drink && !cg.disableCopyCustomizations))
        .forEach(cg => {
          if (cg.canOverride) {
            this.copyCustomizationGroups(cg,
              (c1, c2) => c1.canOverride === c2.canOverride && c1.displayText.toLowerCase() === c2.displayText.toLowerCase(),
              (mg1, mg2) => mg1.displayText.toLowerCase() === mg2.displayText.toLowerCase(),
              (m1, m2) => m1.displayText.toLowerCase() === m2.displayText.toLowerCase(),
              (s1, s2) => s1.displayText.toLowerCase() === s2.displayText.toLowerCase(), recipeContext);
          } else {
            this.copyCustomizationGroups(cg,
              (c1, c2) => c1.canOverride === c2.canOverride && c1.id === c2.id,
              (mg1, mg2) => mg1.id === mg2.id && mg1?.modifiers?.find(m=> m.isDefaultSelected)?.id === mg2?.modifiers?.find(m=> m.isDefaultSelected)?.id && isModifierGroupDefaultSelected(mg1, cg) == isModifierGroupDefaultSelected(mg2, cg),
              (m1, m2) => m1.id === m2.id,
              (s1, s2) => s1.id === s2.id, recipeContext);
          }
        });
    }
  }
  copyCustomizationGroups(scg: CustomizationGroupsEntity,
    customizationGroupComparer: (c1: CustomizationGroupsEntity, c2: CustomizationGroupsEntity) => boolean,
    modifierGroupComparer: (mg1: ModifierGroupsEntity, mg2: ModifierGroupsEntity) => boolean,
    modifierComparer: (m1: ModifiersEntity, m2: ModifiersEntity) => boolean,
    specialInstructionComparer: (s1: SpecialInstructionsEntity, s2: SpecialInstructionsEntity)=>boolean, recipeContext = RecipeContext.CHILD) {
    _.flatMap(recipeContext == RecipeContext.LINKED ? this.linkedChild.customizationGroups : this.child.customizationGroups)
    .filter(cg => customizationGroupComparer(cg, scg)).forEach(destinationCustomizationGroup => {
      scg.modifierGroups.forEach(sourceModifierGroup => {
        const specialInstructionIds = [];
        let destinationModifierGroup = destinationCustomizationGroup?.modifierGroups?.find(mg => modifierGroupComparer(mg, sourceModifierGroup));
        if (destinationModifierGroup) {
          if(destinationModifierGroup.isSelected !== sourceModifierGroup.isSelected) {
            destinationModifierGroup.isSelected = sourceModifierGroup.isSelected;
          }
          const sourceModifier = sourceModifierGroup?.modifiers?.find(s=> s.id === sourceModifierGroup.selectedModifier);
          if (sourceModifier) {
              let destinationModifier = destinationModifierGroup?.modifiers?.find(m => modifierComparer(m, sourceModifier));
              if (destinationModifier)
              {
                  if (destinationModifier.id != destinationModifierGroup.selectedModifier)
                  {
                      destinationModifierGroup.selectedModifier = destinationModifier.id;
                      destinationModifier.isSelected = true;
                  }
                  sourceModifier?.specialInstructions?.forEach(sourceSpecialInstruction => {
                    let destinationSpecialInstruction = destinationModifier?.specialInstructions?.find(s => specialInstructionComparer(s, sourceSpecialInstruction));
                    if (destinationSpecialInstruction)
                    {
                        destinationSpecialInstruction.isEnabled = sourceSpecialInstruction.isEnabled;
                    }
                  });
                  this.updateWhatsOnItForBread(destinationModifierGroup, destinationModifier);
              }
          }
          // Unselect modifiers
          sourceModifierGroup?.modifiers?.filter(sm=>!sm.isSelected && (sm.id || sm.displayText)).forEach(sm => {
            let destinationModifier = destinationModifierGroup?.modifiers?.find(m => modifierComparer(m, sm));
            if(destinationModifier) {
              destinationModifier.isSelected = false;
              destinationModifier?.specialInstructions?.forEach(si => {
                si.isEnabled=false;
              });
            }
          });
        }
      });
    });
  }
  updateWhatsOnItForBread(modGroup: ModifierGroupsEntity, modifier: ModifiersEntity) {
    if (modGroup.displayText.includes('Bread')) {
      if (modGroup.isSelected === true) {
        if (!modifier.specialInstructionsModifier) {
          modifier.specialInstructionsModifier = [];
        }
        if (modifier.specialInstructions) {
          const spIn = [];
          modifier?.specialInstructions?.filter(s=>s.isEnabled)?.forEach(sp => spIn.push(sp.id));
          modifier.specialInstructionsModifier.push({ modifierId: modifier.id, specialInstructionId: spIn });
          modGroup.specialInstructionId = [];
          modGroup.specialInstructionId.push(...spIn);
        } else {
          modifier.specialInstructionsModifier.push({ modifierId: modifier.id, specialInstructionId: [] });
        }
        modifier.isSelected = true;
      }
    }
  }
  private setDefaultChildValues(successResponse: any, recipeContext: RecipeContext) {
    const child = _.cloneDeep(successResponse.recipe)
    child.ingredientsList = _.cloneDeep(successResponse.ingredients);
    const select_item_gtm = sessionStorage.getItem('select_item_gtm');
    if(select_item_gtm) {
      sessionStorage.removeItem('select_item_gtm');
      const selectItemPayload = this.gtmService.getGTMEventPayloadFromRecipe(child, 'select_item');
      this.gtmService.sendGenericEvent({ ecommerce: null });
      this.gtmService.sendGenericEvent(selectItemPayload);
    }
    child.customizationGroups.forEach(
      (custGroup) => {
        if (custGroup.hasNoneSelectionGroup) {
          if (custGroup.hasNoneSelectionGroup && !custGroup.modifierGroups.some(s => s.id === custGroup.id)) {
            const date = new Date();
            date.setDate(date.getDate() + 3);
            custGroup.modifierGroups.push({
              id: custGroup.id, displayText: custGroup.noneSelectionGroupName,
              name: custGroup.noneSelectionGroupName,
              sortIndex: undefined, availability: { effective: date.toString() }, modifiers: undefined, modiferNotAvailable: undefined,
            });
          }
        }
        if (custGroup.modifierGroups && custGroup.modifierGroups.length > 0) {
          custGroup.modifierGroups.forEach(
            (modGroup) => {
              if (isModifierGroupDefaultSelected(modGroup, custGroup)) {
                // # 25441 if the custGroup.canOverride false and isDefaultSelected = true to allow soft drink to be default selected.
                if (custGroup.type === 2 && !custGroup.canOverride) {
                  modGroup.isSelected = false;
                } else {
                  modGroup.isSelected = true;
                }
                modGroup.selectedModifier = modGroup.modifiers.find(x => x.isDefaultSelected).id;
                if (modGroup.displayText.includes('Bread')) {
                  modGroup.modifiers.forEach(mod => {
                    if (mod.isDefaultSelected === true) {
                      mod.isSelected = true;
                    }
                  });
                }
              }
            }
          );
        }
      }
    );

    if (child.associatedItems !== undefined) {
      child.hasSide = child.associatedItems.some(a => a.type === 1);
      child.hasDrink = child.associatedItems.some(a => a.type === 2);
    }

    child.selectedScreen = this.pageId;
    switch (recipeContext) {
      case RecipeContext.CHILD:
        this.child = child;
        break;
      case RecipeContext.LINKED:
        this.linkedChild = child;
        this.linkedChild.selectedScreen = 1;
        break;
      default:
        this.child = child;
        break;
    }
  }

  setOffersDefaultChildValues(child) {
    child.customizationGroups.forEach(
      (custGroup) => {
        if (custGroup.hasNoneSelectionGroup) {
          if (custGroup.hasNoneSelectionGroup && !custGroup.modifierGroups.some(s => s.id === custGroup.id)) {
            const date = new Date();
            date.setDate(date.getDate() + 3);
            custGroup.modifierGroups.push({
              id: custGroup.id, displayText: custGroup.noneSelectionGroupName,
              name: custGroup.noneSelectionGroupName,
              sortIndex: undefined, availability: { effective: date.toString() }, modifiers: undefined, modiferNotAvailable: undefined,
            });
          }
        }
        if (custGroup.modifierGroups && custGroup.modifierGroups.length > 0) {
          custGroup.modifierGroups.forEach(
            (modGroup) => {
              if (isModifierGroupDefaultSelected(modGroup, custGroup)) {
                  modGroup.isSelected = true;
                modGroup.selectedModifier = modGroup.modifiers.find(x => x.isDefaultSelected).id;
                if (modGroup.displayText.includes('Bread')) {
                  modGroup.modifiers.forEach(mod => {
                    if (mod.isDefaultSelected === true) {
                      mod.isSelected = true;
                    }
                  });
                }
              }
            }
          );
        }
      }
    );

    if (child.associatedItems !== undefined) {
      child.hasSide = child.associatedItems.some(a => a.type === 1);
      child.hasDrink = child.associatedItems.some(a => a.type === 2);
    }
    if(!child?.recipeContext || child?.recipeContext !== RecipeContext.LINKED) {
      child.selectedScreen = this.pageId;
    }
    return child;
  }

  getOfferChildRecipeToCustomize(childRecipeId): Promise<any> {
    const apiEndpoint = ApiRoutes.LOCATIONS + this.locationId + ApiRoutes.CHILD_RECIPE + '/' + childRecipeId;
    return this.proxy.httpGet(apiEndpoint).toPromise().then(
      (success) => {
        const successResponse = <ChildRecipeResponse>success.body.valueOf();
        if (successResponse !== undefined && successResponse.recipe !== undefined) {
          successResponse.recipe = this.setOffersDefaultChildValues(successResponse.recipe);
          return successResponse;
        }
      },
      (error) => {
        this.logger.logError(error);
      }
    );
  }

  getMatchesRecipe(receipe: any, categoryId: Number, parentRecipeId: Number, childRecipeId: Number) {
    this.logger.log('Recipe', receipe);
    const recpSel = receipe.recipes.find((rec) => {
      return rec.recipeId === parentRecipeId;
    }).childRecipes.find((childrec) => {
      return childrec.childRecipeId === childRecipeId;
    });
    if (recpSel) {
      return true;
    } else {
      return false;
    }
  }

  calculateCaloriesAndPrice(child: ChildRecipeEntity): ChildRecipeEntity {
    // set to the default price of the child recipe. This is already passed with the recipe object. The reason that
    // we use this as the default and then adjust it based on user selected modifiers is because the default modifier
    // prices may not add up the the default price for a given location. Some locations may have different pricing
    // for a variety of reasons (usually higher pricing) than what the default modidfiers would add up to.
    // if(child.linkedRecipe?.customizationGroups?.length) {

    // }
    this.isOffer = false;
    let totalPrice = child.recipeBasicInfo.price;
    const getOfferData = (this.paramSource === 'offer' || this.paramSplOpt === 'offer' ||
    (child.isOfferAdded && (this.paramSource === 'offer' || this.paramSplOpt === 'offer') )) ? true : false;
    if (this.offerService.getSelectedOffer(getOfferData) && (((this.paramSource === 'offer'
      || this.paramSplOpt === 'offer') && child.isOfferAdded) || ((this.paramSource === 'offer' || this.paramSplOpt === 'offer')
      && child.isOfferAdded === undefined)) ) {
      this.isOffer = true;
      this.offerService.isBuyOneGetOneFree(this.offerService.offerSelectedData, child.id, child?.parentRecipeId);
    }

    if (((this.isReward && this.rewardItemService.currentRewardItem) || this.isOrderReward) && child.context !== RecipeContext.LINKED) {
      totalPrice = 0;
    }

    if ((this.isOffer && !this.offerService.isBuyOneGetOne) ||
     ((this.paramSource === 'offer' || this.paramSplOpt === 'offer') && child.isOfferAdded)) {
      if ((child.offerCouponCode !== null && child.offerCouponCode !== undefined)
      || (this.isOffer && !this.offerService.isBuyOneGetOne)) {
        totalPrice = this.calculateOfferPrice(this.offerService.offerSelectedData, child.recipeBasicInfo.price);
      }
    }

    let totalCalories = 0;
    if (child !== undefined && child.customizationGroups !== undefined) {
      child.customizationGroups.forEach(
        (customizationGroup) => {
          if (customizationGroup.modifierGroups && customizationGroup.modifierGroups.length > 0) {
            let isGroupSelected = false;
            let defaultCalories = 0;
            if ( customizationGroup.customizationSubGroups && customizationGroup.customizationSubGroups.length > 0 ) {
              const selectedSubGroupEntity = this.customizationSubGroupService.calculateSubGroupPrice(customizationGroup);
              selectedSubGroupEntity.forEach(subGroup => {
                const modifiedCustSubPrice = (subGroup.subQuantity * subGroup.subPrice)
                                          + (subGroup.quantity * subGroup.modifierUnitPrice);
                totalPrice += modifiedCustSubPrice;
              });
            }
            customizationGroup.modifierGroups.forEach(
              (group) => {
                const selectedModifier = group.selectedModifier;
                if ((group.isSelected && group.modifiers)) {
                  isGroupSelected = true;
                    group.modifiers.forEach(
                      (modifier) => {
                        if (!this.customizationSubGroupService.isModifierExistInSubgroup(customizationGroup, group)) {
                          if (group.isSelected && modifier.id === Number(selectedModifier)) {
                            if(!group.linkedRecipeId) {
                              if (!customizationGroup.canOverride) {
                                totalPrice += modifier.price ? modifier.price : 0;
                              } else {
                                // totalPrice += modifier.price ? modifier.price : 0;
                                if (!((this.isReward && this.rewardItemService.currentRewardItem) || this.isOrderReward)) {
                                  totalPrice += modifier.price ? modifier.price : 0;
                                  // Checking for recipeTypeId to be A la carte
                                  // TODO: We have to move this magic number 1 to an ENUM
                                  if (child.recipeTypeId === 1) {
                                    totalPrice -= child.recipeBasicInfo.price;
                                  }
                                }
                              }
                            }
                          }
                        }

                        if (modifier.id === selectedModifier && modifier.ingredients.length > 0) {
                          const ingredients = modifier.ingredients;
                          ingredients.forEach(
                            (item) => {
                              const ingridientId = item.ingredientId;
                              const multiplier = item.multiplier !== undefined ? item.multiplier : 0;
                              if (child.ingredientsList) {
                                const ingredient = child.ingredientsList.find(ie => ie.id === ingridientId);
                                if (ingredient !== undefined && ingredient.nutritionInfo) {
                                  totalCalories += ingredient.nutritionInfo.calories ?
                                    this.menuUtil.RoundCalories(ingredient.nutritionInfo.calories * multiplier) : 0;
                                }
                              }
                            }
                          );
                        }
                      }
                    );
                } else if (isModifierGroupDefaultSelected(group, customizationGroup) && customizationGroup.type === 2 && group.modifiers) {
                  group.modifiers.forEach(
                    (modifier) => {
                      if (modifier.isDefaultSelected) {
                        const drinksIsSelectedFlag = customizationGroup.modifierGroups.find(custflag => custflag.isSelected);
                        if(!group.linkedRecipeId) {
                          if (!drinksIsSelectedFlag) {
                            if (!customizationGroup.canOverride) {
                              totalPrice += modifier.price ? modifier.price : 0;
                            } else {
                                if (!((this.isReward && this.rewardItemService.currentRewardItem) || this.isOrderReward)) {
                                  totalPrice += modifier.price ? modifier.price : 0;
                                  totalPrice -= child.recipeBasicInfo.price;
                              }
                            }
                          }
                        }
                        const ingredients = modifier.ingredients;
                        ingredients.forEach(
                          (item) => {
                            const ingridientId = item.ingredientId;
                            const multiplier = item.multiplier ? item.multiplier : 1;
                            const ingredient = child.ingredientsList.find(ie => ie.id === ingridientId);
                            if (ingredient !== undefined && ingredient.nutritionInfo) {
                              defaultCalories = ingredient.nutritionInfo.calories ?
                                this.menuUtil.RoundCalories(ingredient.nutritionInfo.calories * multiplier) : 0;
                            }
                          }
                        );
                      }
                    }
                  );
                }
              }
            );
            if (!isGroupSelected) {
              totalCalories += defaultCalories;
            }
          }
        }
      );
    }
    const ingredientsEntity = <IngredientsEntity[]>child.ingredientsList;
    if (child.ingredients && child.ingredients.length > 0) {
      child.ingredients.forEach(
        (inMulti: IngredientMultiplier) => {
          const ingredient = ingredientsEntity.find(
            ing => ing.id === inMulti.ingredientId
          );
          const multiplier = inMulti.multiplier;
          if (ingredient !== undefined && ingredient.nutritionInfo) {
            totalCalories += ingredient.nutritionInfo.calories ?
              this.menuUtil.RoundCalories(ingredient.nutritionInfo.calories * multiplier) : 0;
            this.logger.log('cal', totalCalories);
          }
        });
    }
    child.totalCalories = totalCalories;
    child.totalPrice = totalPrice;
    if(child.context == RecipeContext.CHILD && child?.linkedRecipe?.customizationGroups?.length) {
      child.totalCalories += child.linkedRecipe?.totalCalories ?? 0;
      const linkedCustGroup = child?.customizationGroups?.find(cg => cg.modifierGroups?.find(mg => mg.linkedRecipeId == child.linkedRecipe.id));
      const defaultLinkedCustomizationGroupPrice = linkedCustGroup?.modifierGroups?.find(mg => isModifierGroupDefaultSelected(mg, linkedCustGroup))?.modifiers?.find(m=> m.isDefaultSelected)?.unitPrice ?? 0;
      if(child.recipeTypeId == RecipeType.AlaCarte) {
        child.totalPrice += child.linkedRecipe.totalPrice;
      } else {
        child.totalPrice = child.totalPrice + child.linkedRecipe.recipeBasicInfo.price + child.linkedRecipe.totalPrice - defaultLinkedCustomizationGroupPrice;
      }
    }
    this.menuUtil.RoundCalories(child.totalCalories);
    return child;
  }

  childRecipeChanged(data: ChildRecipeEntity, fromComponent: any) {
    data.context = data.context ? data.context : RecipeContext.CHILD;
    if (!(fromComponent instanceof CustomizationService)) {
      this.child = _.cloneDeep(data);
    }
    this.child = <ChildRecipeEntity>this.menuUtil.updateRecipeIncludedInfo(this.child);
    sessionStorage.setItem('childRecipeId', this.child?.id?.toString());
    this.calculateCaloriesAndPrice(this.child);
    this.checkIfDrinkExist(this.child);
    // this.checkOfferSidesExits();
    // Handle page refreshes and second go around when current reward item is wiped out
    // this.updateAndEmitParentRecipe();
    if(this.child.useReward && this.parent?.recipes?.length >1) {
      this.parent.recipes = this.parent?.recipes.filter(r => r.id == this.child.id);
      this.updateAndEmitParentRecipe();
    }
    this.childRecipe.emit({ childRecipe: this.child, from: fromComponent });
  }
  isQuantityAttainedForRestrictedCustomizationGroups() {
    if(this?.quantityRestrictedCustomizationGroupList?.length) {
      return  !this.quantityRestrictedCustomizationGroupList.some(cg => !cg.minQuantityAttained);
    } else {
      return true;
    }

  }
  checkWhataSize(childRecipe: ChildRecipeEntity) {
    if(childRecipe.isWhatasizeApplied) {
      const modGroup = childRecipe?.customizationGroups?.map(cust =>
      cust?.modifierGroups?.find(mod => mod.whatASizable === true && mod.isSelected === true));
      modGroup?.forEach(mGrp => {
        const index = mGrp?.modifiers?.findIndex(m => m.id === mGrp.selectedModifier);
        if(!isNaN(index) && index + 1 < mGrp.modifiers.length) {
          childRecipe.isWhatasizeApplied = false;
          return;
        }
      });
    }
  }

  myOrderSummaryCalAndPrice(data: ChildRecipeEntity): ChildRecipeEntity {
    this.child = _.cloneDeep(data);
    this.calculateCaloriesAndPrice(this.child);// TODO: check when to do this for linked recipe
    return this.child;

  }

  getFavoriteItem(pageId: number, itemIndex: number,  recipeContext = RecipeContext.CHILD, isUpsell = false) {
    // Get the child recipe to edit from redux
    // the index is the array index of the specific child recipe to customize.
    // after finding the child recipe object, make sure to use the child.emit to let the details
    // and summary page to know which child recipe item to customize.
    this.activeFavorite.subscribe((Succ) => {
      if (Succ) {
        this.orderdCollection = Succ as Order;
        const editedRecipe = this.orderdCollection.childRecipes[itemIndex];
        if(recipeContext == RecipeContext.CHILD) {
          editedRecipe.selectedScreen = pageId;
          this.child = _.cloneDeep(editedRecipe);
          this.childRecipeChanged(editedRecipe, this);
        } else {
          if(editedRecipe?.linkedRecipe?.customizationGroups?.length) {
            this.linkedChild = _.cloneDeep(editedRecipe?.linkedRecipe);
            if(!isUpsell) {
              this.linkedChild.customizationGroups = this.linkedChild.customizationGroups.filter(cg => !cg.isUpsell);
            }
            this.linkedChildChanged(this.linkedChild, this);
          } else {
            this.setDefaultChildRecipeId(editedRecipe.linkedRecipe?.id, RecipeContext.LINKED, editedRecipe.orderItemId, isUpsell);
          }
        }
      }
    });
  }

  getOrderItem(pageId: number, itemIndex: number, recipeContext = RecipeContext.CHILD, isUpsell = false) {
    // TODO the order object from redux should be subscribed too.
    // The order object is an array of child recipe entities.
    // the index is the array index of the specific child recipe to customize.
    // after finding the child recipe object, make sure to use the child.emit to let the details
    // and summary page to know which child recipe item to customize.
    this.activeOrder.subscribe((Succ) => {
      this.orderdCollection = Succ as Order;
      if (this.orderdCollection !== undefined && this.orderdCollection !== null) {
        const editedRecipe = this.orderdCollection.childRecipes[itemIndex];
        if (editedRecipe !== undefined) {
          if(recipeContext == RecipeContext.CHILD) {
            editedRecipe.selectedScreen = pageId;
            this.child = _.cloneDeep(editedRecipe);
            this.childRecipeChanged(editedRecipe, this);
          } else {
            if(editedRecipe.linkedRecipe?.customizationGroups?.length) {
              this.linkedChild = _.cloneDeep(editedRecipe?.linkedRecipe);
              if(!isUpsell) {
                this.linkedChild.customizationGroups = this.linkedChild.customizationGroups.filter(cg => !cg.isUpsell);
              }
              this.linkedChildChanged(this.linkedChild, this);
            } else {
              this.setDefaultChildRecipeId(editedRecipe.linkedRecipe?.id, RecipeContext.LINKED, editedRecipe.orderItemId, isUpsell);
            }
          }
        }
      }
    });
  }

  removeUnavaialbleRecipe() {
    this.activeOrder.subscribe((Succ) => {
      this.orderdCollection = Succ as Order;
      const removeItemFlag = localStorage.getItem('removeItemFromCart');
      if (removeItemFlag === 'true') {
        const childObjIndex = this.orderdCollection.childRecipes.
          findIndex(o => o.isRecipeNotAvailable === true);
        if (childObjIndex >= 0) {
          if (this.orderdCollection.childRecipes[childObjIndex].offerCouponCode) {
            this.offerService.clearOffers(true);
          }
          localStorage.removeItem('removeItemFromCart');
          this.orderdCollection.childRecipes.splice(childObjIndex, 1);
          this.ngRedux.dispatch({ type: Actions.UPDATEORDER, data: this.orderdCollection });
        }
      }
    });
  }

  getChild() {
    return this.child;
  }

  // TODO right now this is only adding new items to the current order.
  // need to change this to allow an edit of a child recipe to update for an
  // active existing order item if the user is doing an edit, or a update
  // for a favorite order if the user is editting a child recipe item from
  //  a favorite order.
  updateOrder(childRecipeEntity: ChildRecipeEntity, customizationObj: any, keepOrderItemId: boolean = false) {

    switch (customizationObj.source) {
      case Sources.FAVORITE:
        this.activeFavorite.subscribe((resp) => {
          this.order = resp ? <Order>resp : new Order;

          if (this.order.childRecipes === undefined) {
            this.order.childRecipes = [];
          }
        });

        if (this.isReward && this.rewardItemService.currentRewardItem) {
          childRecipeEntity.useReward = true;
          // Adding a flag to track Reward utilization
          this.order.isRewardAdded = true;
        }

        if (!this.order.childRecipes.find(s => s.orderItemId === childRecipeEntity.orderItemId)) {
          this.child = <ChildRecipeEntity>this.menuUtil.updateRecipeIncludedInfo(childRecipeEntity);
          this.order.childRecipes.push(this.child);
        } else {
          const objIndex = this.order.childRecipes.findIndex(c => c.orderItemId === childRecipeEntity.orderItemId);
          this.child = <ChildRecipeEntity>this.menuUtil.updateRecipeIncludedInfo(childRecipeEntity);
          this.child.UnavailableModifierGroups = undefined;
          this.child.isDrinksNotAvailable = undefined;
          this.order.childRecipes[objIndex] = this.child;
        }
        this.ngRedux.dispatch({ type: Actions.UPDATEFAVORITE, data: this.order });
        break;

      case Sources.MENU:
      case Sources.REWARD:
      case Sources.OFFER:
        this.activeOrder.subscribe((resp) => {
          this.order = resp ? <Order>resp : new Order;

          if (this.order.childRecipes === undefined) {
            this.order.childRecipes = [];
          }
        });

        if (this.isReward && this.rewardItemService.currentRewardItem) {
          childRecipeEntity.useReward = true;
          // Adding a flag to track Reward utilization
          this.order.isRewardAdded = true;
        }
        // if (customizationObj.source.toLowerCase() === Sources.OFFER && !proceedWithoutOffer) {
        //   childRecipeEntity.isOfferAdded = true;
        // }

        if (!this.order.childRecipes.find(s => s.orderItemId === childRecipeEntity.orderItemId)) {
          this.child = <ChildRecipeEntity>this.menuUtil.updateRecipeIncludedInfo(childRecipeEntity);
          this.order.childRecipes.push(this.child);
        } else {
          const objIndex = this.order.childRecipes.findIndex(c => c.orderItemId === childRecipeEntity.orderItemId);
          this.child = <ChildRecipeEntity>this.menuUtil.updateRecipeIncludedInfo(childRecipeEntity);
          this.child.UnavailableModifierGroups = undefined;
          this.child.isDrinksNotAvailable = undefined;
          this.order.childRecipes[objIndex] = this.child;
        }
        this.order.isFavorite = false;
        const gtmRecipePayloadNonOrder = this.gtmService.getGTMCartPayloadFromRecipe(this.child);
        this.gtmService.sendGenericEvent({ecommerce:null} as CartGTM);
        this.gtmService.sendGenericEvent(gtmRecipePayloadNonOrder);
        this.ngRedux.dispatch({ type: Actions.UPDATEORDER, data: this.order });
        break;

      case Sources.ORDER:
        this.activeOrder.subscribe((resp) => {
          this.order = resp ? <Order>resp : new Order;
        });
        if (this.order.childRecipes === undefined) {
          this.order.childRecipes = [];
        }
        if (!this.order.childRecipes.find(s => s.orderItemId === childRecipeEntity.orderItemId)) {
          this.child = <ChildRecipeEntity>this.menuUtil.updateRecipeIncludedInfo(childRecipeEntity);
          this.order.childRecipes.push(this.child);
          // remove the recipeId from redux - if user edits item from order summary and selects different recipe
          const orderItemId = sessionStorage.getItem('orderItemId');
          const objIndex = this.order.childRecipes.findIndex(c => c.orderItemId === Number(orderItemId));
          this.order.childRecipes.splice(objIndex, 1);
        } else {
          const objIndex = this.order.childRecipes.findIndex(c => c.orderItemId === childRecipeEntity.orderItemId);
          this.child = <ChildRecipeEntity>this.menuUtil.updateRecipeIncludedInfo(childRecipeEntity);
          this.child.UnavailableModifierGroups = undefined;
          this.child.isDrinksNotAvailable = undefined;
          this.order.childRecipes[objIndex] = this.child;
          // remove the recipeId from redux - if user edits item from order summary and selects different recipe
          const orderItemId = sessionStorage.getItem('orderItemId');
          if (!this.order.childRecipes.find(s => s.orderItemId === Number(orderItemId))) {
            const objIndex1 = this.order.childRecipes.findIndex(c => c.orderItemId === Number(orderItemId));
            this.order.childRecipes.splice(objIndex1, 1);
          }

        }
        const gtmRecipePayloadOrder = this.gtmService.getGTMCartPayloadFromRecipe(this.child);
        this.gtmService.sendGenericEvent({ecommerce:null} as CartGTM);
        this.gtmService.sendGenericEvent(gtmRecipePayloadOrder);
        if(!keepOrderItemId) {
          sessionStorage.removeItem('orderItemId');
        }
        this.order.isFavorite = false;
        this.ngRedux.dispatch({ type: Actions.UPDATEORDER, data: this.order });
        break;
      default:
        break;
    }
  }

  checkIfDrinkExist(child: ChildRecipeEntity) {
    // Start- Fix for Bug #25447 - code to remove the drinks which are nt available for this location
    const orderItemId = sessionStorage.getItem('orderItemId');
    if (orderItemId !== undefined) {
      child.isRecipeNotAvailable = undefined;
      child.customizationGroups.forEach(
        (custGroup) => {
          custGroup.modifierGroups = custGroup.modifierGroups.filter(modifier => !modifier.modiferNotAvailable);
        });
    }
    // End- Fix for Bug #25447 - code to remove the drinks which are nt available for this location
    let temp = false;
    if (child.recipeTypeId === RecipeType.MediumMeal || child.recipeTypeId === RecipeType.SmallMeal || child.recipeTypeId === RecipeType.Breakfast
       || child.recipeTypeId === RecipeType.KidsMeal || child.recipeTypeId === RecipeType.Bundle) {
      const customizationGroup = child.customizationGroups?.length ? child.customizationGroups.find(c => c.type === 2) : null;
      const modifiers = customizationGroup.modifierGroups ? customizationGroup.modifierGroups.find(c => c.isSelected === true) : null;
      if (modifiers !== undefined) {
        return temp;
      } else {
        temp = true;
      }
    }
    return temp;
  }

  // Calculate price for offers based on offer discount type.
  calculateOfferPrice(offer: Offer, basePrice) {
    const pOverrideFilter = this.getMatchingPriceFilterForRecipe(offer);
    if (pOverrideFilter) {
      switch (pOverrideFilter?.discountType) {
        case OfferDiscountType.Amount:
          return Math.max(basePrice - pOverrideFilter.discount, 0);
        case OfferDiscountType.Override:
          return pOverrideFilter.discount;
        case OfferDiscountType.Percent:
          return Math.max(basePrice - (basePrice * (pOverrideFilter.discount / 100)), 0);
        default:
          return basePrice;
      }
    } else {
      return basePrice;
    }
  }

  getMatchingPriceFilterForRecipe(offerSelected: Offer): OfferPriceOverrideFilter {
    if (offerSelected && offerSelected.rules && offerSelected.rules.priceOverride) {
      const offersPriceOverride = offerSelected.rules.priceOverride;
      const discountPrice = offersPriceOverride.find((pride) => {
        return pride.discount;
      });
      const recpdt = discountPrice?.recipes?.find((rec) => {
        return rec.recipeId === this.child.parentRecipeId;
      });
      // const custGroupdt = this.child.customizationGroups.find((cg) => cg.id == discountPrice.customizationGroupId);

      if (recpdt) {
        const chidrec = recpdt.childRecipes.find((childrec) => {
          return childrec.childRecipeId === this.child.id;
        });
        if (chidrec) {
          return discountPrice;
        } else {
          return null;
        }
      } else {
        return null;
      }
    } else {
      return null;
    }
  }

  getOfferRecipes(recipesList) {
    if (this.offerService.getSelectedOffer(true)) {
      const offerCat = this.offerService.offerSelectedData.rules.filterMenu.find((sel) => {
        return sel.categoryId === this.childRecipeId;
      });
      if (offerCat) {
        const offerRecipes = offerCat.recipes.find((rec) => {
          return rec.recipeId === this.recipeId;
        });
        if (recipesList && offerRecipes) {
          const filterRecpList = recipesList.filter((dt) => {
            return offerRecipes.childRecipes.some((ch) => {
              return ch.childRecipeId === dt.id;
            });
          });
          return filterRecpList;
        } else { return recipesList; }
      } else { return recipesList; }
    } else {
      return recipesList;
    }
  }

  getModifierDefaultSelected(customizationGroups) {
    return customizationGroups.filter((cust) => {
      return cust.modifierGroups.some((mgroup) => {
        return isModifierGroupDefaultSelected(mgroup, cust);
      });
    });
  }

  getOfferFilterMenu() {
    if (this.offerService.offerSelectedData) {
      if (this.offerService.offerSelectedData.rules.filterMenu &&
        this.offerService.offerSelectedData.rules.filterMenu.length === 1) {
          this.offerService.offerSelectedData.rules.filterMenu[0].categoryId = this.child.categoryId;
      }
      const offerCat = this.offerService.offerSelectedData.rules.filterMenu.find((sel) => {
        return sel.categoryId === this.child.categoryId;
      });
      if (offerCat) {
        const offerRec = offerCat.recipes.find((rec) => { // TODO: overriten
          return rec.recipeId === this.child.parentRecipeId;
        });
        if (offerRec) {
          this.offerFilterMenu = offerRec.childRecipes.find((childrec) => { // TODO: overriten
            return childrec.childRecipeId === this.child.id;
          });
          return this.offerFilterMenu;
        } else { return null; }
      } else { return null; }
    } else {
      return null;
    }
  }

  setOfferAlertButtonsAction(offerMsg) {
    const OfferAlertButtonsAction = [];
    offerMsg.buttons.forEach(act => {
      act.action = act.action.replace(':', '-');
      act.dtitle = act.title.toLowerCase().replace(' ', '');
      OfferAlertButtonsAction.push({
        'title': act.dtitle,
        'action': act.action,
        'displayTitle': act.title
      });
    });
    return OfferAlertButtonsAction;
  }

  getModifierData(type: Number) {
    const customizationGroup = this.child.customizationGroups ? this.child.customizationGroups.find(c => c.type === type) : null;
    return customizationGroup && customizationGroup.modifierGroups ?
      customizationGroup.modifierGroups.find(c => c.isSelected === true) : null;
  }

  setReward(flag: boolean) {
    this.isReward = flag;
  }

  setOffer(flag: boolean) {
    this.isOffer = flag;
  }

  sendValue(data: any) {
    this.toggleWhatsOnIt.emit(data);
  }

}
